import React from 'react'
import {bindActionCreators} from 'redux'
import {connect} from 'react-redux'

import TitleBar from './ui/TitleBar'
import {colors} from '../utils/styles'
import {ProgressBar} from './ui/ProgressBar'
import {CheckBox} from './ui/CheckBox'
import {AgDetailsTable} from './ui/AgDetailsTable'
import {confirmPickupDates, inquireDnD, navigateToSearch} from '../state/dnd/actions'
import {searchTodo} from '../state/todolist/actions'
import {genericStringPairProperties, GridUtil} from '../utils/GridUtil'
import InvoiceAndStatusRenderer from './ui/InvoiceAndStatusRenderer'
import {dateTimeOnlyToYYYYMMDD, formatNumber, localIodtToUtc, merge} from '../utils/util'
import {TargetPickupDateRenderer} from './ui/TargetPickupDateRenderer'
import DndChargesRenderer from './ui/DndChargesRenderer'
import NotifyDialog from './ui/NotifyDialog'
import PaymentInfoBox from './ui/PaymentInfoBox'
import PaymentOption from './ui/PaymentOption'
import {showSeeYouLater, startPayment} from '../state/payment/actions'
import Disclaimer from './Disclaimer'
import HiddenSprintPayButton from './ui/HiddenSprintPayButton'
import {CopyrightFooter} from './ui/CopyrightFooter'

const textFieldPromise = import(/* webpackChunkName: 'textField' */'@material/react-text-field')
let TextField, Input

const allColumns = {
  scenario: {headerName: 'Scenario', columnWidth: 105, agColumnProperties: {hide: false}},
  cntrNum: {headerName: 'Container #', columnWidth: 110},
  targetPickupDt: {
    headerName: 'Target Pick up Date',
    columnWidth: 120,
    columnStyles: ['overflowVisible'],
    agColumnProperties: {
      cellEditorFramework: TargetPickupDateRenderer,
      singleClickEdit: true,
      cellRendererFramework: function (params) {
        const data = params.data
        const targetPickupDate = data.targetPickupDt.substring(0, 8)
        if (data.scenario === 'CHANGE TO') {
          const isPUDateChanged = data.originalTargetPickupDt !== data.targetPickupDt
          if (data.datePickerEnabled) {
            return (
                <TextField
                    style={{width: 100}}
                    className={'target-pickup-date'}
                    outlined={false}
                    dense={true}
                >
                  <Input
                      id={'dtpkr' + params.rowIndex}
                      style={{color: isPUDateChanged ? colors.dataChanged : colors.grayMiddle}}
                      value={targetPickupDate}
                      className={'target-pickup-date'}
                      isValid={targetPickupDate}/>
                </TextField>
            )
          } else {
            const style = {
              paddingTop: '3.5px',
              color: isPUDateChanged ? colors.dataChanged : colors.grayMiddle,
              fontWeight: data.payCharges ? 600 : null
            }
            return <div style={style}>{targetPickupDate}</div>
          }
        } else {
          return <div style={{paddingTop: '3.5px'}}>{targetPickupDate}</div>
        }
      }
    }
  },
  lastFreeDt: {headerName: 'Last Free Date', columnWidth: 125},
  demCharge: {
    headerName: 'Demurrage Charges',
    columnWidth: 130,
    agColumnProperties: {cellRendererFramework: DndChargesRenderer},
    suppressSizeToFit: true
  },
  rmcCharge: {
    headerName: 'Reefer Monitoring Charges',
    columnWidth: 150,
    agColumnProperties: {cellRendererFramework: DndChargesRenderer},
    suppressSizeToFit: true
  },
  totalCharges: {
    headerName: 'Total Charges',
    columnWidth: 137,
    agColumnProperties: {cellRendererFramework: DndChargesRenderer},
    suppressSizeToFit: true
  },
  invoiceDetails: {
    headerName: 'Invoice & Status',
    columnWidth: 310,
    agColumnProperties: {cellRendererFramework: InvoiceAndStatusRenderer}
  },
  releaseStatusPair: {
    headerName: 'Release Status',
    columnWidth: 410,
    agColumnProperties: genericStringPairProperties
  }
}

export class DndProcess extends React.Component {
  constructor(props) {
    super(props)
    const platform = props.jumpDirectPlatform || 'PC'
    this.state = {
      currentStep: props.jumpDirectToPay ? 2 : 0,
      samePickupDate: false,
      disableNextButton: !props.jumpDirectToPay,
      rows: [],
      confirmMessage: null,
      confirmTitle: null,
      progressSteps: [
        {label: 'Set or Revise Pick Up Date', size: 190},
        {label: 'Select Payment Option', size: 190},
        {label: 'Confirm', size: 190},
        {label: 'Make Payment', size: 190}
      ],
      paymentOption: props.jumpDirectToPay ? (platform === 'SP' ? 'Pay Now SP' : 'Pay Now') : '',
      platform: platform,
    }
    this.paymentInfoBox = React.createRef()
    this.handleOptionClickedBound = this.handleOptionClicked.bind(this)
    this.waitForImports()
  }

  static getDerivedStateFromProps(props) {
    return {
      paymentWasCancelled: props.errorMessage && props.errorMessage.includes('cancelled')
    }
  }

  async waitForImports() {
    TextField = (await textFieldPromise).default
    Input = (await textFieldPromise).Input
    this.setState({loaded: true})
  }

  componentDidMount() {
    this.splitRowsIntoCurrentAndWhatif()
  }

  onGridReady() {
    this.moveFocusToEditableEmptyPickupDate()
  }

  splitRowsIntoCurrentAndWhatif() {
    const rows = []
    if (this.props.rows) {
      this.props.rows.forEach((rec) => {
        let row = {
          blNum: rec.blNum,
          shpNum: rec.shpNum,
          scenario: rec.canEdit ? 'CURRENT' : ' ',
          cntrNum: rec.cntrNum,
          checkDigit: rec.checkDigit,
          mvmtSpec: rec.mvmtSpec,
          datePickerEnabled: false,
          targetPickupDt: rec.latestPickupDate ? rec.latestPickupDate.relativeDate : '',
          lastFreeDt: rec.lastFreeDate.relativeDate,
          lastRmcFreeDt: rec.lastRmcFreeDt,
          lfdUtc: rec.lfdUtc,
          lhubFcil: rec.lhubFcil,
          cargoGroup: rec.cargoGroup,
          demCharge: rec.demCharge,
          rmcCharge: rec.rmcCharge,
          totalCharges: rec.totalCharges,
          invoiceDetails: rec.invoiceDetails,
          releaseStatusPair: rec.releaseStatusPair,
          releaseStatusPairSave: rec.releaseStatusPair,
          currency: rec.currency,
          loading: false,
          message: null,
          invoices: rec.invoices,
          svvd: rec.svvd,
          vessel: rec.vessel,
          pickupLocation: rec.pickupLocation,
          pickupTz: rec.pickupTz,
          canEdit: rec.canEdit,
          paymentStatus: rec.paymentStatus,
          lastToDoActivityTime: rec.lastToDoActivityTime
        }
        rows.push(row)
        if (row.canEdit) {
          if (this.props.jumpDirectToPay) {
            row = merge(row, {
              scenario: 'CHANGE TO',
              dndConfirm: null,
            })
          } else {
            row = merge(row, {
              scenario: 'CHANGE TO',
              invoiceDetails: [],
              targetPickupDt: (row.targetPickupDt || ''),
              datePickerEnabled: true,
              dndConfirm: null,
              paidAmountWhatIf: row.paidAmount,
              releaseStatusPair: null,
            })
          }
          row.originalInvoiceDetails = rec.invoiceDetails
          row.originalTargetPickupDt = (row.targetPickupDt || '')
          rows.push(row)
        }
      })
    }
    this.setState({rows: rows})
  }

  getTotalOutstanding() {
    return this.getTotalCharges('totalCharges') - this.getTotalPaid()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.dndwhatif !== this.props.dndwhatif) {
      Object.keys(this.props.dndwhatif).forEach((cntrNum) => {
        this.gridUtil.gridApi.forEachNode((node) => {
          if (node.data.cntrNum === cntrNum && node.data.scenario === 'CHANGE TO') {
            const rows = this.props.dndwhatif[cntrNum]
            const newData = node.data
            for (const row of rows) {
              if (row.chargeType === 'DEM') {
                newData.demCharge = row.chargeAmount
                newData.currency = row.currency
                newData.message = row.message
                newData.loading = false
              }
              if (row.chargeType === 'RMC') {
                newData.rmcCharge = row.chargeAmount
              }
            }
            setTimeout(this.moveFocusToEditableEmptyPickupDate.bind(this), 100)
            newData.totalCharges = newData.demCharge + (newData.rmcCharge || 0)
            node.setData(newData)
            this.gridUtil.forceRefreshNode(node)
          }
        })
      })
      this.enableDisableReviewPickupButton()
      if (this.getTotalOutstanding() === 0) {
        this.setState({showNoAddedCharge: true})
      }
    }

    if (this.props.dndconfirm && prevProps.dndconfirm !== this.props.dndconfirm) {
      // When confirming > 1 container, the BL will still be locked while confirm process runs
      this.callStartPayment(!this.props.confirmInput || this.props.confirmInput.length === 1)
    }
  }

  getRowHeight(params) {
    const maxLines = params.data.invoiceDetails ? params.data.invoiceDetails.length : 1
    if (maxLines > 2) {
      // 17.14 is the height of each row, 20 is 2x10px padding top & bottom
      return (maxLines * 17.14) + 20
    }
    return 50
  }

  navigateToSearchAndRefresh() {
    // It is important to refresh current state from IR4.  Found during PRD a case where an exception happened
    // during confirm, and an old unconfirmed CHANGE TO demurrage amount was still hanging around in state
    // and got used to caclulate an incorrect payment amount
    this.props.actions.searchTodo()
    this.props.actions.navigateToSearch()
  }

  handlePreviousClick() {
    if (this.state.currentStep === 0 || this.state.paymentWasCancelled) {
      return this.navigateToSearchAndRefresh()
    }
    if (this.state.currentStep === 2) {
      if (this.props.jumpDirectToPay) {
        this.navigateToSearchAndRefresh()
      } else {
        this.props.actions.searchTodo()  // See comment in navigateToSearchAndRefresh()
      }
    }
    this.setState({currentStep: this.state.currentStep - 1})
  }

  getBlueButtonLabel() {
    const paymentWasCancelled = this.state.paymentWasCancelled
    if (this.props.errorMessage && !paymentWasCancelled) {
      return 'Back to Start'
    }
    const currentStep = this.state.currentStep
    if (currentStep === 2 || paymentWasCancelled) {
      const paymentOption = this.state.paymentOption
      const platform = this.state.platform === 'SP' ? 'SprintPay' : 'PayCargo'
      return (this.props.jumpDirectToPay || paymentWasCancelled)
          ? 'Pay Now with ' + platform
          : 'Confirm Pickup Date and Pay ' +
          (paymentOption.includes('Now')
              ? 'Now with ' + platform
              : (paymentOption.includes('Later'))
                  ? 'Later' : 'Offline')
    }
    if (currentStep === 3) {
      return 'Back to Start'
    }
    return 'Continue'
  }

  shouldDisableBlueButton(totalOutstanding, label) {
    if (label === 'Back to Start') {
      return false
    }
    const currentStep = this.state.currentStep
    if (currentStep === 0 && totalOutstanding < 1) {
      return true
    }
    if ((this.showPaymentEmailOnly() || this.showPaymentFullMode())
        && this.paymentInfoBox.current && !this.paymentInfoBox.current.isValid()) {
      return true
    }
    if (currentStep === 1) {
      return !this.state.paymentOption
    }
    if (currentStep === 2) {
      const isPayingNow = 'Pay Now' === this.state.paymentOption || this.props.jumpDirectToPay
      return totalOutstanding < 1 && isPayingNow
    }
    return this.state.disableNextButton
  }

  handleBlueButtonClick(label) {
    if (label === 'Continue') {
      if (this.state.currentStep === 1 && this.props?.userProfile?.forCosco) {
        // cosco only.  if user did NOT pick pay now with paycargo, then show a warning
        if (this.state.paymentOption !== 'Pay Now') {
          this.setState({showCoscoSprintPay: true})
          return
        }
      }
      this.setState({currentStep: this.state.currentStep + 1})
      return
    }
    if (label.startsWith('Confirm')) {
      this.handleConfirmButtonClicked()
      return
    }
    if (label.startsWith('Pay')) {
      this.callStartPayment(true)
      return
    }
    if (label.startsWith('Back')) {
      this.props.actions.searchTodo()
      this.props.actions.navigateToSearch()
      return
    }
    window.alert('Unknown button: ' + label)
  }

  handleConfirmButtonClicked() {
    const confirmPayload = []
    for (const row of this.state.rows) {
      if (row.scenario === 'CHANGE TO' && row.originalTargetPickupDt !== row.targetPickupDt) {
        const pickupLocalIodt = this.convertPickerDateToLocalIodt(row.targetPickupDt)
        // sanity check
        if (pickupLocalIodt === 'Invalid date') {
          alert('Pickup date is invalid: ' + row.targetPickupDt)
          return
        } else {
          confirmPayload.push({
            blNum: row.blNum,
            cntrNum: row.cntrNum,
            checkDigit: row.checkDigit,
            mvmtSpec: row.mvmtSpec,
            svvd: row.svvd,
            vessel: row.vessel,
            lfdUtc: row.lfdUtc,
            pickupDate: pickupLocalIodt,
            targetPickupDtUtc: localIodtToUtc(pickupLocalIodt, row.pickupTz),
            pickupLocation: row.pickupLocation,
            pickupTz: row.pickupTz,
            lhubFcil: row.lhubFcil,
            cargoGroup: row.cargoGroup,
            shipmentNumber: row.shpNum,
            lastToDoActivityTime: row.lastToDoActivityTime
          })
        }
      }
    }
    if (confirmPayload.length) {
      this.props.actions.confirmPickupDates(confirmPayload)
    }
  }

  handleOptionClicked(option) {
    const platform = (option === 'Pay Now SP' || option === 'Pay Later SP') ? 'SP' : 'PC'
    this.setState({paymentOption: option, platform: platform})
  }

  callStartPayment(lockBl) {
    this.setState({
      currentStep: 3
    })
    const startPaymentInfo = this.getStartPaymentInfo(lockBl)
    if (startPaymentInfo) {
      this.props.actions.startPayment(startPaymentInfo)
    }
  }

  getStartPaymentInfo(lockBl) {
    const paymentInfo = this.paymentInfoBox.current

    const paymentRows = this.state.rows.filter((row) => row.scenario !== 'CURRENT')
    // Reason for !== 'CURRENT' is bc payment containers include:
    // 1. CHANGE TO rows
    // 2. Rows with ' ' scenario (these are non-editable containers like gate-out which are invoice related
    // and were dragged along as a single row).  These have no targetPickupDt
    const cntrData = paymentRows.map(ea => {
      const targetPickupDtUtc = ea.targetPickupDt
          ? localIodtToUtc(this.convertPickerDateToLocalIodt(ea.targetPickupDt), ea.pickupTz)
          : null
      return {
        blNum: ea.blNum,
        cntrNum: ea.cntrNum,
        checkDigit: ea.checkDigit,
        mvmtSpec: ea.mvmtSpec,
        svvd: ea.svvd,
        vessel: ea.vessel,
        lfdUtc: ea.lfdUtc,
        targetPickupDtUtc: targetPickupDtUtc,
        pickupLocation: ea.pickupLocation,
        pickupTz: ea.pickupTz,
        lhubFcil: ea.lhubFcil,
        cargoGroup: ea.cargoGroup,
        invoices: ea.invoices,
        lastToDoActivityTime: ea.lastToDoActivityTime
      }
    })
    if (cntrData.some(ea => 'Invalid date' === ea.targetPickupDtUtc)) {
      alert('targetPickupDtUtc is invalid')
    } else if (!paymentRows.length) {
      alert('No containers to pay')
    } else if (cntrData.some(eaCntr => eaCntr.invoices.some(inv => 'TBD' === inv.invoice))) {
      alert(
          'A required invoice must be created before a payment can be made. Please try again later. If the problem remains, please contact '
            + this.getCarrierLabel() + '.')
    } else {
      const anyRow = paymentRows[0]
      const emailToSend = paymentInfo.emailToSend()
      const paymentOption = this.state.paymentOption
      return {
        amount: paymentInfo.getTotalCharge(),
        currency: anyRow.currency,
        blNumber: anyRow.blNum,
        cntrData: cntrData,
        sendEmail: !!emailToSend,
        emailAddress: emailToSend,
        customerRef: paymentInfo.getCustomerRefNum(),
        includeReceipt: paymentInfo.includeReceipt(),
        offlinePayment: paymentOption.includes('Offline') || paymentOption.includes('Later'),
        platform: this.state.platform,
        lockBl: lockBl
      }
    }
    return null
  }

  handleSamePickupCheck() {
    this.setState({samePickupDate: !this.state.samePickupDate})
  }

  cellChanged(event) {
    if (event.type === 'cellValueChanged') {
      if (this.state.samePickupDate) {
        this.gridUtil.gridApi.forEachNode((node) => {
          if (node.data.scenario === 'CHANGE TO') {
            this.whatifCallForNode(node, event)
          }
        })
      } else {
        this.whatifCallForNode(event.node, event)
      }

      this.enableDisableReviewPickupButton()
    }
  }

  convertPickerDateToLocalIodt(dateFromPicker) {
    // input expected to be in format like 04/23/2022
    const pickupIodt = dateTimeOnlyToYYYYMMDD(dateFromPicker)
    return ('Invalid date' === pickupIodt)
        ? pickupIodt
        : pickupIodt + '235900.000'
  }

  whatifCallForNode(node, event) {
    const pickupIodt = this.convertPickerDateToLocalIodt(event.newValue)
    const valid = (pickupIodt !== 'Invalid date')

    const row = node.data
    row.loading = valid
    row.targetPickupDt = event.newValue
    node.setData(row)
    this.gridUtil.forceRefreshNode(node)
    if (valid) {
      const payload = {
        cntrNum: row.cntrNum,
        pickupDate: pickupIodt,
        blNum: row.blNum,
        shipmentNumber: row.shpNum,
        lhubFcil: row.lhubFcil,
        cargoGroup: row.cargoGroup
      }
      this.props.actions.inquireDnD(payload)
    }
  }

  canEditPickupDate(params) {
    // remember scenario for gate-out is ' '
    if (params.data.scenario !== 'CHANGE TO') {
      return false
    }
    if (this.state.currentStep > 0) {
      return false
    }
    return !params.data.loading
  }

  enableDisableReviewPickupButton() {
    let enableButton = true
    this.gridUtil.gridApi.forEachNode((node) => {
      if (node.data.scenario === 'CHANGE TO') {
        if (node.data.message !== null || node.data.loading) {
          enableButton = false
        }
        if (!node.data.targetPickupDt) {
          enableButton = false
        }
      }
    })
    this.setState({disableNextButton: !enableButton})
  }

  getPrevLabel() {
    const progressSteps = this.state.progressSteps
    switch (this.state.currentStep) {
      case 1:
        return 'Back to ' + progressSteps[0].label
      case 2:
        return this.props.jumpDirectToPay ? 'Back to Start' : 'Back to ' + progressSteps[1].label
      default:
        return 'Back to Start'
    }
  }

  getColumns(displayReefer) {
    const columns = {...allColumns}
    columns.scenario.agColumnProperties.hide = this.state.currentStep !== 0
    columns.targetPickupDt.agColumnProperties.editable = this.canEditPickupDate.bind(this)
    columns.lastFreeDt.headerName = 'Demurrage Last Free Date'
    if (!displayReefer) {
      columns.totalCharges = merge(columns.totalCharges, {headerName: 'Total Demurrage Charges'})
      delete columns.demCharge
      delete columns.rmcCharge
    }
    return columns
  }

  getRows() {
    const whatIfRows = this.state.rows.filter((row) => row.scenario === 'CHANGE TO')
    if (this.state.currentStep === 0) {
      whatIfRows.forEach((row) => {
        row.payCharges = false
        row.datePickerEnabled = true
        row.invoiceDetails = []
        row.releaseStatusPair = null
      })
      return this.state.rows
    }
    const laterStepRows = this.state.rows.filter((row) => row.scenario !== 'CURRENT')
    laterStepRows.forEach((row) => {
      if (row.scenario === 'CHANGE TO') {
        row.payCharges = this.state.currentStep === 1
        row.datePickerEnabled = false
        row.invoiceDetails = row.originalInvoiceDetails
        row.releaseStatusPair = row.releaseStatusPairSave
      }
    })
    return laterStepRows
  }

  getTopMessage(totalOutstanding) {
    let msg
    let style = {paddingTop: 20, fontSize: 16, color: colors.blue, fontWeight: 500}
    if (this.props.errorMessage) {
      msg = this.props.errorMessage
      style.color = colors.red
      style.fontWeight = 600
    } else {
      const currentStep = this.state.currentStep
      const paymentOption = this.state.paymentOption
      const platform = this.state.platform === 'SP' ? 'SprintPay' : 'PayCargo'
      if (currentStep === 0) {
        msg = 'Choose the Target Pick Up Date by utilizing the calendar and the system will calculate the demurrage charges.'
      }
      if (currentStep === 1) {
        msg = "Please select the payment option and click 'Continue' to proceed to review and confirm."
      }
      if (currentStep === 2) {
        const reviewChrgsMsg = 'Please review the summary of the demurrage charges.'
        const fillNotifyMsg = 'please fill out the “Notification Information” section and then click on the "'
            + this.getBlueButtonLabel() + '" button.'
        if (paymentOption.includes('Now')) {
          msg = <span>{reviewChrgsMsg} To proceed with payment, {fillNotifyMsg}</span>
        }
        if (paymentOption.includes('Later')) {
          msg = <span>{reviewChrgsMsg} To pay later, {fillNotifyMsg}</span>
        }
        if (paymentOption === 'Pay Offline') {
          msg = <span>{reviewChrgsMsg} To pay offline, {fillNotifyMsg}</span>
        }
      }
      if (currentStep === 3) {
        if (this.props.clearedAmount) {
          msg =
              <span>Total charge in the amount of {this.props.clearedAmount} has been paid through {platform}.
                {platform === 'PayCargo' ? ' The cargo will be released within 30-90 minutes.' : ''}</span>
        }
        if (paymentOption === 'Pay Later') {
          msg = <span>Total charge in the amount of {formatNumber(
              totalOutstanding)} USD will be paid later.</span>
        }
        if (paymentOption === 'Pay Offline') {
          msg = <span>Total charge in the amount of {formatNumber(
              totalOutstanding)} USD will be paid offline.</span>
        }
      }
    }
    return <div style={style}>{msg}</div>
  }

  moveFocusToEditableEmptyPickupDate() {
    const rows = this.state.rows || []
    let targetIndex = rows.findIndex(ea =>
        ea.canEdit
        && ea.scenario === 'CHANGE TO'
        && !ea.targetPickupDt
    )
    if (targetIndex > -1) {
      this.gridUtil.gridApi.startEditingCell({
        rowIndex: targetIndex,
        colKey: 'targetPickupDt'
      })
    }
  }

  getTitle() {
    return 'Real Time Release > ' + this.state.progressSteps[this.state.currentStep].label
  }

  getTableHeight(negativeOutstanding) {
    const maxHeight = 479 // For 1920x1080 with maximized chrome and no bookmarks bar
    const minHeight = 150
    // Payment Options pane dictates maximum overall height so if startingHeight is set correctly
    // then position of the disclaimer will remain constant when traversing all steps
    let height = Math.min(window.innerHeight - (943 - maxHeight), maxHeight)
    if (this.showMultiSelectCheckbox(this.props.rows)) {
      height = height - 24
    }
    if (this.showPaymentFullMode()) {
      height = height - 149
    }
    if (this.showPaymentEmailOnly()) {
      height = height - 87
    }
    if (negativeOutstanding) {
      height = height - 27
    }
    return Math.max(height, minHeight)
  }

  getPaymentInformationBox(displayReefer, totalDemCharges, totalRmcCharges, totalCharges, totalPaid,
      totalOutstanding, currency) {
    const canEdit = this.state.currentStep === 2 || this.state.paymentWasCancelled
    return (
        <div style={{paddingTop: 10}}>
          <PaymentInfoBox
              displayReefer={displayReefer}
              fullMode={this.showPaymentFullMode()}
              showEmail={this.showPaymentEmailOnly()}
              showCustomerRef={this.state.paymentOption.includes('Now')}
              ref={this.paymentInfoBox}
              totalDemCharges={totalDemCharges}
              totalRmcCharges={totalRmcCharges}
              totalCharges={totalCharges}
              totalPaid={totalPaid}
              totalOutstanding={totalOutstanding}
              currency={currency}
              disabled={!canEdit}

              // Do not remove the onChange! It triggers the dndProcess to enable/disable the Pay Charges
              // button depending if the e-mail settings are valid or not
              onChange={(value) => {
                this.setState({paymentInfoChanged: value})
              }}
          />
        </div>
    )
  }

  showPaymentFullMode() {
    return this.state.currentStep > 1
  }

  showPaymentEmailOnly() {
    return false
  }

  openPayCargoTab() {
    window.open(this.props.ePayResult.data.paycargoUrl, 'payCargo')
  }

  getSeeYouLaterBody() {
    return <span>You will be directed to a new PayCargo page to complete payment. If a new tab is not
      automatically opened on your browser, then click on
      this <a style={{color: colors.blue, cursor: 'pointer'}}
              onClick={this.openPayCargoTab.bind(this)}>PayCargo</a> hyperlink to continue.</span>
  }

  getTotalCharges(type) {
    let totalCharges = 0
    this.getRows().forEach((row) => {
      if (row.scenario !== 'CURRENT') {
        const demurrageToUse = row[type]
        totalCharges += demurrageToUse
      }
    })
    return totalCharges
  }

  getTotalPaid() {
    let totalPaid = 0
    const processedInvoices = new Set()
    this.getRows().forEach((row) => {
      if (row.scenario !== 'CURRENT') {
        row.invoices.forEach((invRow) => {
          if (!processedInvoices.has(invRow.invoice)) {
            const invoiceAmount = invRow.invoiceAmount
            if ((invoiceAmount > 0 && invRow.paid >= invoiceAmount)
                || (invoiceAmount < 0 && invRow.paid <= invoiceAmount)) {
              totalPaid += invoiceAmount
            }
            processedInvoices.add(invRow.invoice)
          }
        })
      }
    })
    return totalPaid
  }

  findCurrency(rows) {
    const found = (rows || []).find((r) => r.currency)
    return found ? found.currency : 'USD'
  }

  render() {
    if (!TextField) {
      return null
    }
    const myOnGridMount = (params) => {
      if (params) {
        this.gridUtil = new GridUtil(params)
        if (this.props.onGridMount) {
          this.props.onGridMount(params)
        }
      }
    }
    const currency = this.findCurrency(this.state.rows)
    const totalCharges = this.getTotalCharges('totalCharges')
    const totalDemCharges = this.getTotalCharges('demCharge')
    const totalRmcCharges = this.getTotalCharges('rmcCharge')
    const totalPaid = this.getTotalPaid()
    const totalOutstanding = totalCharges - totalPaid
    const negativeOutsanding = totalOutstanding < 0
    const rows = this.props.rows
    const firstRow = (rows && rows.length && rows[0]) || {}
    const forCosco = this.props?.userProfile?.forCosco
    const displayReefer = firstRow.cargoGroup === 'RF' && !forCosco
    const rightDetails = this.getHeaderRightDetails(rows)
    const height = forCosco ? 'calc(100vh - 142px)' : null
    return (
      <React.Fragment>
        <div>
          <TitleBar
              titleStyle={{height: 78}}
              showBackButton={false}
              title={this.getTitle()}
              rightDetails={[rightDetails]}
          />
          <div style={{width: 1338, maxWidth: '94%', margin: 'auto', padding: '20px', height: height}}>
            <div style={{height: 10}}/>
            <ProgressBar
                steps={this.state.progressSteps}
                position={this.state.currentStep}
            />
            {this.getTopMessage(totalOutstanding)}
            <div style={{height: 10}}/>
            {this.getTableOrPaymentOption(displayReefer, rows, negativeOutsanding, myOnGridMount)}
            {this.getPaymentInformationBox(displayReefer, totalDemCharges, totalRmcCharges, totalCharges,
                totalPaid, totalOutstanding, currency)}
            {this.getNegativeOutstandingMessage(negativeOutsanding)}
            {this.getNavigationButtons(totalOutstanding)}
            <div style={{height: 10}}/>
            <Disclaimer fontSize={14} userProfile={this.props.userProfile}/>
          </div>
          {this.getSeeYouLaterNotifyDialog()}
          {this.getNoNewChargesDialog()}
          {this.triggerSprintPayIfNeeded()}
          {this.getCoscoSprintPayDialog()}
        </div>
        <CopyrightFooter forCosco={this.props?.userProfile?.forCosco}/>
      </React.Fragment>
    )
  }

  triggerSprintPayIfNeeded() {
    if (this.state.platform !== 'SP'
        || this.state.currentStep !== 3
        || !this.props.startedPaymentInfo
        || this.props.errorMessage
    ) {
      return null
    }
    const paymentInfo = this.props.startedPaymentInfo
    return <HiddenSprintPayButton
        amount={paymentInfo.amount}
        paymentReqNo={paymentInfo.paymentReqNo}
        customerRef={paymentInfo.customerRef}
        notifyEmail={paymentInfo.sendEmail ? paymentInfo.emailAddress : null}
        notes={paymentInfo.notes}
        env={this.props.userProfile.env}
    />
  }

  getTableOrPaymentOption(displayReefer, rows, negativeOutsanding, myOnGridMount) {
    if (this.state.currentStep === 1 && !this.props.jumpDirectToPay) {
      return <PaymentOption option={this.state.paymentOption}
                            onChange={this.handleOptionClickedBound}
                            userProfile={this.props.userProfile}
      />
    }
    return <>
      {this.getApplyToAllCheckbox(rows)}
      <AgDetailsTable
          columns={this.getColumns(displayReefer)}
          rowsData={this.getRows()}
          headerHeight={51}
          height={this.getTableHeight(negativeOutsanding)}
          rowHeightFn={this.getRowHeight.bind(this)}
          onGridMount={myOnGridMount.bind(this)}
          onGridReady={this.onGridReady.bind(this)}
          suppressRowTransform={true}
          onCellValueChanged={this.cellChanged.bind(this)}
          suppressHorizontalScroll={true}
      />
    </>
  }

  getSeeYouLaterNotifyDialog() {
    return this.props.showSeeYouLater && <NotifyDialog
        titleText={'Complete Payment using PayCargo'}
        bodyText={this.getSeeYouLaterBody()}
        dialogInsideModal={true}
        showCancel={true}
        showOk={false}
        cancelCallback={() => {
          this.props.actions.showSeeYouLater(false)
        }}
    />
  }

  getCarrierLabel() {
    return this.props?.userProfile?.forCosco ? 'COSCO' : 'OOCL'
  }

  getNoNewChargesDialog() {
    return this.state.showNoAddedCharge && <NotifyDialog
        titleText={'Note'}
        bodyText={'There is no new charge owed through the selected date. Please check with the terminal or '
            + this.getCarrierLabel() + " import customer service for respective terminal's gate schedule. Alternatively, you can select a later pick up date to proceed."}
        dialogInsideModal={true}
        showCancel={false}
        showOk={true}
        okCallback={() => {
          this.setState({showNoAddedCharge: false})
        }}
    />
  }

  getCoscoSprintPayDialog(){
    return this.state.showCoscoSprintPay && <NotifyDialog
        titleText={'Warning!'}
        bodyText={"Select Pay Now with PayCargo to maintain the real time release of shipment when paying demurrages. Any other option will result in standard release times with processing delay."}
        dialogInsideModal={true}
        showCancel={true}
        showOk={true}
        okText={('Continue With '+this.state.paymentOption).toUpperCase()}
        okCallback={() => {
          this.setState({showCoscoSprintPay: false, currentStep: this.state.currentStep + 1})
        }}
        cancelCallback={()=> {
          this.setState({showCoscoSprintPay: false})
        }}
    />
  }

  getApplyToAllCheckbox(rows) {
    return this.showMultiSelectCheckbox(rows) &&
        <CheckBox isChecked={this.state.samePickupDate}
                  handleClick={this.handleSamePickupCheck.bind(this)}
                  label='Use the same target pick up date for all containers'/>
  }

  getNavigationButtons(totalOutstanding) {
    const buttonStyle = {minWidth: 198}
    return <div style={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      marginTop: 10
    }}>
      {this.getBlueButton(buttonStyle, totalOutstanding)}
      <div style={{width: 20}}/>
      {this.getWhiteButton(buttonStyle)}
    </div>
  }

  getWhiteButton(buttonStyle) {
    if (!this.state.paymentWasCancelled) {
      if (this.props.errorMessage || this.state.currentStep === 3) {
        return null
      }
    }
    return <button className='white-button-round'
                   style={buttonStyle}
                   onClick={this.handlePreviousClick.bind(this)}
    >{this.getPrevLabel()}</button>
  }

  getBlueButton(buttonStyle, totalOutstanding) {
    const label = this.getBlueButtonLabel()
    return <button className='blue-button-round'
                   style={buttonStyle}
                   onClick={this.handleBlueButtonClick.bind(this, label)}
                   disabled={this.shouldDisableBlueButton(totalOutstanding, label)}
    >{label}</button>
  }

  getNegativeOutstandingMessage(negativeOutsanding) {
    let msg = 'Invoice for the previous target pick up date has been paid. For refund, please contact'
    const forCosco = this.props?.userProfile?.forCosco
    msg += forCosco ? ' COSCO at' : ' OOCL at'
    let ref = forCosco ? 'https://coscoshippingna.freshdesk.com' : 'NATAR@oocl.com'
    const text = ref
    if (!forCosco) {
      ref = 'mailto:' + ref
    }
    return negativeOutsanding && <div
        style={{
          fontSize: '14px',
          color: colors.red,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          paddingTop: 10,
          fontWeight: 600
        }}>{msg}&nbsp;<a href={ref} style={{color: colors.blue, cursor: 'pointer'}}>{text}</a>.
    </div>
  }

  showMultiSelectCheckbox(rows) {
    return rows && rows.length > 1 && this.state.currentStep === 0
  }

  getHeaderRightDetails(rows) {
    if (!rows || !rows.length) {
      return ''
    }
    const anyRow = rows[0]
    return anyRow.blNum + ' · ' + anyRow.pickupLocation
  }
}

function mapStateToProps(state) {
  return {
    dndwhatif: state.dnd.whatif,
    dndconfirm: state.dnd.confirm,
    confirmInput: state.dnd.confirmInput,
    jumpDirectToPay: state.dnd.jumpDirectToPay,
    jumpDirectPlatform: state.dnd.jumpDirectPlatform,
    ePayResult: state.payment.ePayResult,
    startedPaymentInfo: state.payment.paymentInfo,
    showSeeYouLater: state.payment.showSeeYouLater,
    errorMessage: state.payment.errorMessage,
    clearedAmount: state.payment.clearedAmount,
    rows: state.dnd.rowsToShow,
    userProfile: state.userProfile,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      navigateToSearch, inquireDnD, confirmPickupDates, startPayment, showSeeYouLater, searchTodo
    }, dispatch), dispatch
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DndProcess)